// src/screens/ActiveMatchesScreen.js (refactor)
// Admin-only "control room" to monitor in-progress matches, inspect participants,
// and run administrative actions (force-complete / cancel). 
// Drop-in replacement for your current ActiveMatches.js.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  ScrollView,
  Alert,
  Platform,
} from 'react-native';

import { useError } from '../components/ErrorContext';

// Firebase
import { db, app } from '../../firebaseConfig';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
  getDocs,
  doc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

// Hooks & UI chrome
import { useAdminAuth } from '../hooks/useAdminAuth';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';

// Optional: central status constants if present
let STATUS = {
  ACTIVE: 'active',
  LIVE: 'live',
  AWAITING_VERIFICATION: 'awaiting_verification',
  READY_FOR_PAYOUT: 'ready_for_payout',
  PAID: 'paid',
  CANCELED: 'canceled',
};
try {
  // eslint-disable-next-line global-require
  STATUS = require('../constants/status').STATUS || STATUS;
} catch (_) {}

const PAGE_SIZE = 50;
const DEFAULT_STATUSES = [STATUS.ACTIVE, STATUS.LIVE];

const fmt = (ts) => {
  const d = ts?.toDate?.() || (ts ? new Date(ts) : null);
  if (!d || Number.isNaN(+d)) return '—';
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

function Chip({ label, onPress, active = false }) {
  return (
    <TouchableOpacity onPress={onPress} style={[styles.chip, active && styles.chipActive]}>
      <Text style={[styles.chipTxt, active && styles.chipTxtActive]}>{label}</Text>
    </TouchableOpacity>
  );
}

function StreamingBadge({ streams, hasLiveStream }) {
  const live = useMemo(() => {
    if (hasLiveStream) return true;
    if (!Array.isArray(streams)) return false;
    return streams.some((s) => !!s?.live);
  }, [streams, hasLiveStream]);

  return (
    <View style={[styles.badge, live ? styles.badgeLive : styles.badgeDim]}> 
      <Text style={styles.badgeTxt}>{live ? 'LIVE' : 'No Stream'}</Text>
    </View>
  );
}

export default function ActiveMatchesScreen({ navigation }) {
  const { user, isAdmin, isLoading } = useAdminAuth();

  // Filters / state
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState([...DEFAULT_STATUSES]);
  const [gameFilter, setGameFilter] = useState('ALL');
  const [regionFilter, setRegionFilter] = useState('ALL');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paging, setPaging] = useState(false);
  const lastDocRef = useRef(null);
  const unsubRef = useRef(null);

  const functions = getFunctions(app, 'us-central1'); // adjust region if needed
  // adminCompleteEvent/adminCancelEvent resolve the id across both the
  // tournaments and matches collections (getEventRef), so match ids work here.
  const forceCompleteFn = httpsCallable(functions, 'adminCompleteEvent');
  const cancelMatchFn = httpsCallable(functions, 'adminCancelEvent');

  // Attach live query
  const attach = async (afterDoc = null) => {
    const clauses = [];

    // Status filter (use "in" for up to 10 values)
    if (statusFilter && statusFilter.length) {
      clauses.push(where('status', 'in', statusFilter));
    }

    clauses.push(orderBy('updatedAt', 'desc'));
    clauses.push(limit(PAGE_SIZE));

    const baseQ = query(collection(db, 'matches'), ...clauses);

    if (!afterDoc) {
      unsubRef.current?.();
      setLoading(true);
      unsubRef.current = onSnapshot(
        baseQ,
        (snap) => {
          const list = snap.docs.map((d) => ({ id: d.id, ...d.data(), _ref: d }));
          setRows(list);
          lastDocRef.current = snap.docs.length ? snap.docs[snap.docs.length - 1] : null;
          setLoading(false);
        },
        (err) => {
          console.error('ActiveMatches onSnapshot error', err);
          setLoading(false);
        }
      );
    } else {
      setPaging(true);
      const snap = await getDocs(query(collection(db, 'matches'), ...clauses, startAfter(afterDoc)));
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data(), _ref: d }));
      setRows((prev) => prev.concat(list));
      lastDocRef.current = snap.docs.length ? snap.docs[snap.docs.length - 1] : null;
      setPaging(false);
    }
  };

  // Re-attach on status filter change
  useEffect(() => {
    attach();
    return () => unsubRef.current?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(statusFilter)]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return rows.filter((m) => {
      if (gameFilter !== 'ALL' && (m.game || '') !== gameFilter) return false;
      if (regionFilter !== 'ALL' && (m.region || '') !== regionFilter) return false;
      if (!s) return true;
      const hay = [
        m.name,
        m.tournamentName,
        m.game,
        m.region,
        ...(Array.isArray(m.participants) ? m.participants.map((p) => p?.teamName || p?.name || p?.username) : []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(s);
    });
  }, [rows, search, gameFilter, regionFilter]);

  const confirm = (title, msg, onYes) => {
    if (Platform.OS === 'web') {
      // simple confirm fallback
      // eslint-disable-next-line no-alert
      if (window.confirm(`${title}\n\n${msg}`)) onYes?.();
      return;
    }
    Alert.alert(title, msg, [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Yes', style: 'destructive', onPress: onYes },
    ]);
  };

  const forceComplete = async (m) => {
    confirm('Force complete match?', 'This will end the match and move it to verification/payout flows.', async () => {
      try {
        await forceCompleteFn({ eventId: m.id });
      } catch (e) {
        console.error('forceComplete failed', e);
        Alert.alert('Error', e?.message || 'Failed to force-complete');
      }
    });
  };

  const cancelMatch = async (m) => {
    confirm('Cancel match?', 'This will cancel the match and notify participants.', async () => {
      try {
        await cancelMatchFn({ eventId: m.id, reason: 'Canceled by admin from Active Matches' });
      } catch (e) {
        console.error('cancelMatch failed', e);
        Alert.alert('Error', e?.message || 'Failed to cancel');
      }
    });
  };

  const DETAIL_ROUTE = 'MatchDetail'; // If you use EventDetailScreen instead, change to that route name
  const viewMatch = (m) => {
    navigation.navigate(DETAIL_ROUTE, { matchId: m.id, eventId: m.eventId || null });
  };

  if (isLoading) {
    return (
      <SafeAreaView style={styles.safeArea}> 
        <View style={styles.centerBox}><ActivityIndicator color="#FCD000" /></View>
        <Footer navigation={navigation} />
      </SafeAreaView>
    );
  }

  if (!isAdmin) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.centerBox}> 
          <Text style={styles.denied}>Admins only</Text>
        </View>
        <Footer navigation={navigation} />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <ScrollView contentContainerStyle={styles.container}>
        <Text style={styles.title}>Active Matches (Admin)</Text>

        {/* Filters */}
        <View style={styles.filtersCard}>
          <TextInput
            style={styles.search}
            placeholder="Search by team, player, tournament…"
            placeholderTextColor="#777"
            value={search}
            onChangeText={setSearch}
          />

          <View style={styles.rowWrap}>
            {/* Status chips */}
            <Chip
              label={`Active ${statusFilter.includes(STATUS.ACTIVE) ? '✓' : ''}`}
              active={statusFilter.includes(STATUS.ACTIVE)}
              onPress={() => setStatusFilter((prev) => (prev.includes(STATUS.ACTIVE)
                ? prev.filter((s) => s !== STATUS.ACTIVE)
                : [...prev, STATUS.ACTIVE]))}
            />
            <Chip
              label={`Live ${statusFilter.includes(STATUS.LIVE) ? '✓' : ''}`}
              active={statusFilter.includes(STATUS.LIVE)}
              onPress={() => setStatusFilter((prev) => (prev.includes(STATUS.LIVE)
                ? prev.filter((s) => s !== STATUS.LIVE)
                : [...prev, STATUS.LIVE]))}
            />
            <Chip
              label={`Awaiting Verif ${statusFilter.includes(STATUS.AWAITING_VERIFICATION) ? '✓' : ''}`}
              active={statusFilter.includes(STATUS.AWAITING_VERIFICATION)}
              onPress={() => setStatusFilter((prev) => (prev.includes(STATUS.AWAITING_VERIFICATION)
                ? prev.filter((s) => s !== STATUS.AWAITING_VERIFICATION)
                : [...prev, STATUS.AWAITING_VERIFICATION]))}
            />
          </View>

          <View style={styles.rowWrap}>
            <Chip label={`Game: ${gameFilter}`} onPress={() => {
              const list = ['ALL', 'Marvel Rivals', 'Valorant', 'CS2', 'R6'];
              const idx = list.indexOf(gameFilter);
              const next = list[(idx + 1) % list.length];
              setGameFilter(next);
            }} />
            <Chip label={`Region: ${regionFilter}`} onPress={() => {
              const list = ['ALL', 'NA', 'EU', 'APAC'];
              const idx = list.indexOf(regionFilter);
              const next = list[(idx + 1) % list.length];
              setRegionFilter(next);
            }} />
          </View>
        </View>

        {/* Table */}
        {loading ? (
          <ActivityIndicator color="#FCD000" />
        ) : (
          filtered.map((m) => (
            <View key={m.id} style={styles.card}>
              <View style={{ flex: 1, gap: 2 }}>
                <View style={styles.rowBetween}>
                  <Text style={styles.rowTitle}>{m.name || m.tournamentName || `Match ${m.id.slice(-6)}`}</Text>
                  <StreamingBadge streams={m.streams} hasLiveStream={m.hasLiveStream} />
                </View>
                <Text style={styles.rowSub}>
                  {m.game || '—'} • {m.region || '—'} • Status: {m.status || '—'}
                </Text>
                {!!m.participants?.length && (
                  <Text style={styles.rowSubSmall}>
                    {m.participants.map((p) => p?.teamName || p?.name || p?.username).filter(Boolean).join('  vs  ')}
                  </Text>
                )}
                <Text style={styles.rowMeta}>
                  Started {fmt(m.startedAt)} • Updated {fmt(m.updatedAt)}
                </Text>
              </View>

              <View style={styles.actionsRow}>
                <TouchableOpacity onPress={() => viewMatch(m)} style={[styles.btn, styles.btnPrimary]}>
                  <Text style={styles.btnTxtPrimary}>View</Text>
                </TouchableOpacity>
                <TouchableOpacity onPress={() => forceComplete(m)} style={[styles.btn, styles.btnWarn]}>
                  <Text style={styles.btnTxt}>Force Complete</Text>
                </TouchableOpacity>
                <TouchableOpacity onPress={() => cancelMatch(m)} style={[styles.btn, styles.btnDanger]}>
                  <Text style={styles.btnTxt}>Cancel</Text>
                </TouchableOpacity>
              </View>
            </View>
          ))
        )}

        {!!rows.length && (
          <TouchableOpacity
            style={[styles.loadMore, paging && { opacity: 0.6 }]}
            disabled={paging || !lastDocRef.current}
            onPress={() => lastDocRef.current && attach(lastDocRef.current)}
          >
            <Text style={styles.loadMoreText}>{paging ? 'Loading…' : 'Load more'}</Text>
          </TouchableOpacity>
        )}
      </ScrollView>

      <Footer navigation={navigation} />
      <NavBar navigation={navigation} />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: '#0B0B0D' },
  container: { padding: 16, gap: 12 },
  centerBox: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  denied: { color: '#fff', fontSize: 16 },
  title: { color: '#fff', fontSize: 24, fontWeight: '700', marginBottom: 4 },

  filtersCard: { backgroundColor: '#141419', borderRadius: 12, padding: 12, gap: 10 },
  search: {
    backgroundColor: '#1B1B21', color: '#fff', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10,
  },
  rowWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },

  chip: { paddingHorizontal: 12, paddingVertical: 8, borderRadius: 999, backgroundColor: '#1F1F27' },
  chipActive: { backgroundColor: '#2a2a36' },
  chipTxt: { color: '#ddd', fontSize: 12 },
  chipTxtActive: { color: '#fff', fontWeight: '700' },

  card: { backgroundColor: '#141419', borderRadius: 12, padding: 12, gap: 8, marginTop: 10 },
  rowBetween: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  rowTitle: { color: '#fff', fontSize: 16, fontWeight: '600' },
  rowSub: { color: '#aaa', fontSize: 12 },
  rowSubSmall: { color: '#888', fontSize: 12, marginTop: 2 },
  rowMeta: { color: '#666', fontSize: 11, marginTop: 4 },

  actionsRow: { flexDirection: 'row', gap: 8, marginTop: 8 },
  btn: { paddingHorizontal: 12, paddingVertical: 10, borderRadius: 8, backgroundColor: '#1F1F27' },
  btnTxt: { color: '#fff', fontWeight: '600' },
  btnPrimary: { backgroundColor: '#FCD000' },
  btnWarn: { backgroundColor: '#5c4b00' },
  btnDanger: { backgroundColor: '#5c1f1f' },
  btnTxtPrimary: { color: '#0B0B0D', fontWeight: '800' },

  badge: { paddingHorizontal: 8, paddingVertical: 4, borderRadius: 8 },
  badgeTxt: { fontSize: 10, fontWeight: '700' },
  badgeLive: { backgroundColor: '#E83F36' },
  badgeDim: { backgroundColor: '#2a2a36' },

  loadMore: { alignSelf: 'center', marginVertical: 16, borderRadius: 999, paddingHorizontal: 16, paddingVertical: 10, backgroundColor: '#1F1F27' },
  loadMoreText: { color: '#ddd', fontWeight: '600' },
});
